/*global google*/

import * as React from "react";
import * as ReactDOM from "react-dom";
import * as PropTypes from "prop-types";
import { MapComponentProps } from "./map";
import { getOffsetFromParent } from "../../util";
import { Location, mapLocation } from "./location";

export enum RichMarkerPosition {
    TOP_LEFT = 1,
    TOP,
    TOP_RIGHT,
    LEFT,
    MIDDLE,
    RIGHT,
    BOTTOM_LEFT,
    BOTTOM,
    BOTTOM_RIGHT
}

export interface PassedThroughMarkerProps {
    anchor?: RichMarkerPosition;
    position: Location;
}

interface RichMarkerProps<T extends HTMLElement> extends MapComponentProps, PassedThroughMarkerProps {
    content: React.RefForwardingComponent<T, {}>
    onClick?: () => void;
}

interface RichMarkerOptions extends PassedThroughMarkerProps {
    position: google.maps.LatLng | google.maps.LatLngLiteral
}

declare class richMarker extends google.maps.OverlayView {
    setOptions(options: RichMarkerOptions): void;
}

export class RichMarker<T extends HTMLElement = HTMLElement> extends React.Component<RichMarkerProps<T>>{

    static propTypes = {
        //TODO: MapComponentProps
        anchor: PropTypes.oneOf([
            RichMarkerPosition.TOP_LEFT, RichMarkerPosition.TOP, RichMarkerPosition.TOP_RIGHT,
            RichMarkerPosition.LEFT, RichMarkerPosition.MIDDLE, RichMarkerPosition.RIGHT,
            RichMarkerPosition.BOTTOM_LEFT, RichMarkerPosition.BOTTOM, RichMarkerPosition.BOTTOM_RIGHT
        ]),
        //TODO:
        //position:
        content: PropTypes.oneOfType([PropTypes.func, PropTypes.node])
    }

    static displayName = "GoogleMaps.RichMarker";

    private readonly onClick = () => this.props.onClick && this.props.onClick();

    private readonly content: HTMLDivElement = (() => {
        var content = document.createElement('div');
        content.addEventListener('click', this.onClick);
        return content;
    })();

    private marker?: richMarker;
    private contentRef = React.createRef<T>();


    componentDidMount() {
        // Need to create class in here, because we need the google map scripts loaded.
        class richMarker extends google.maps.OverlayView {
            private markerWrapper: HTMLDivElement;

            constructor(options: RichMarkerOptions, private marker: HTMLDivElement, private markerAnchor?: React.RefObject<T>) {
                super();
                this.setValues(options);
                this.markerWrapper = document.createElement("div");
                this.markerWrapper.style.position = "absolute";
                this.markerWrapper.style.zIndex = '-999999';
                this.markerWrapper.appendChild(marker);
            }

            public setOptions(options: RichMarkerOptions) {
                this.setValues(options);
            }

            public getMarker() {
                return this.marker;
            }

            onAdd() {
                let panes = this.getPanes();
                panes.floatPane.appendChild(this.markerWrapper);
            }

            onRemove() {
                if (this.markerWrapper && this.markerWrapper.parentNode) {
                    this.markerWrapper.parentNode.removeChild(this.markerWrapper);
                }
            }

            anchor_changed() {
                this.draw();
            }

            position_changed() {
                this.draw();
            }

            draw() {
                if (!this.markerWrapper) return;

                let projection = this.getProjection();
                if (!projection) return;

                let latLng = this.get('position');
                if (!(latLng instanceof google.maps.LatLng))
                    latLng = new google.maps.LatLng(latLng.lat, latLng.lng);
                var pos = projection.fromLatLngToDivPixel(latLng);

                var offset = this.getOffset();
                this.markerWrapper.style.top = (pos.y + offset.height) + 'px';
                this.markerWrapper.style.left = (pos.x + offset.width) + 'px';

                var height = this.marker.offsetHeight;
                var width = this.marker.offsetWidth;

                if (width !== this.get('width')) {
                    this.set('width', width);
                }

                if (height !== this.get('height')) {
                    this.set('height', height);
                }
            }

            private getOffset() {
                let offset = new google.maps.Size(0, 0);
                if (!this.marker) return offset;

                let offsetAnchor = (this.markerAnchor && this.markerAnchor.current) || this.marker;

                let width = offsetAnchor.offsetWidth;
                let height = offsetAnchor.offsetHeight;
                let anchor = this.get('anchor') as RichMarkerPosition || RichMarkerPosition.TOP_LEFT;

                switch (anchor) {
                    case RichMarkerPosition.TOP_LEFT:
                        break;
                    case RichMarkerPosition.TOP:
                        offset.width = -width / 2;
                        break;
                    case RichMarkerPosition.TOP_RIGHT:
                        offset.width = -width;
                        break;
                    case RichMarkerPosition.LEFT:
                        offset.height = -height / 2;
                        break;
                    case RichMarkerPosition.MIDDLE:
                        offset.height = -height / 2;
                        offset.width = -width / 2;
                        break;
                    case RichMarkerPosition.RIGHT:
                        offset.width = -width;
                        offset.height = -height / 2;
                        break;
                    case RichMarkerPosition.BOTTOM_LEFT:
                        offset.height = -height;
                        break;
                    case RichMarkerPosition.BOTTOM:
                        offset.width = -width / 2;
                        offset.height = -height;
                        break;
                    case RichMarkerPosition.BOTTOM_RIGHT:
                        offset.width = -width;
                        offset.height = -height;
                        break;
                }

                // Maybe a bug, why force the not null?
                let anchorOffset = getOffsetFromParent(offsetAnchor, this.marker.offsetParent!);
                offset.width -= anchorOffset.x;
                offset.height -= anchorOffset.y;

                return offset;
            }
        }

        this.marker = new richMarker(this.getOptions(), this.content, this.contentRef);
        this.marker.setMap(this.props.map);
    }

    componentWillUnmount() {
        this.marker && this.marker.setMap(null);
        this.content.removeEventListener('click', this.onClick);
    }

    componentDidUpdate() {
        this.marker && this.marker.setOptions(this.getOptions());
    }


    render() {
        let Content = React.forwardRef(this.props.content);
        let portal = ReactDOM.createPortal(<Content ref={this.contentRef} />, this.content);
        this.marker && this.marker.draw();
        return portal;
    }

    getComponent() {
        return this.marker;
    }

    private getOptions(): RichMarkerOptions {
        const {
            map,
            children,
            content,
            position,
            onClick,
            ...props
        } = this.props;

        return {
            position: mapLocation(position),
            ...props
        }
    }

}
